import React from "react";
import { connect } from "react-redux";
import { Format, DataList } from "@redriver/cinnamon";
import { getSheetState, useSheetCalcInfo } from "features/Sheets";
import {
  CalcTableType,
  FunderTypeDescription,
} from "features/../../../shared/constants/enums";
import { useTranslation } from "react-i18next";

const currencyAmount = (value) => (
  <Format.Number
    value={value}
    format={process.env.CURRENCY_CODE}
    decimalPlaces={2}
    thousandSeparator
  />
);

const unitAmount = (value) => (
  <Format.Number
    value={value || 0}
    decimalPlaces={{ min: 0, max: process.env.USE_MINOR_CURRENCY ? 3 : 5 }}
    thousandSeparator
    suffix={process.env.USE_MINOR_CURRENCY && process.env.MINOR_CURRENCY}
    prefix={!process.env.USE_MINOR_CURRENCY && process.env.MAJOR_CURRENCY}
  />
);

const CalculationBreakdown = ({ rawCalculation }) => {
  const { t } = useTranslation("capitalCalc");
  const { t: tCtt } = useTranslation("enums", { keyPrefix: "calcTableType" });
  const { isMssa } = useSheetCalcInfo();
  rawCalculation = rawCalculation || {};
  const breakdown = rawCalculation.breakdown || {};

  const servicesCost = breakdown.servicesCost || {};
  const capitalCalcCosts = breakdown.capitalCalcCosts || {};
  const schedule = breakdown.systemSchedule || {};
  const paperInclusiveCosts = breakdown.paperInclusiveCosts || {};
  const fmvs = breakdown.fmvDetails || [];

  if (!servicesCost || !capitalCalcCosts || !schedule || !paperInclusiveCosts) {
    return null;
  }

  const calcTableTypeDescription = {
    [CalcTableType.Standard]: tCtt(CalcTableType.Standard),
    [CalcTableType.SoftwareAssignment]: tCtt(CalcTableType.SoftwareAssignment),
  };

  return (
    <div className="calculation-breakdown">
      <h3>Calculation breakdown</h3>

      <h4>Summary</h4>
      {dlPair(
        "Minimum Quarterly Payment Logic",
        rawCalculation.quarterlyMinimumPaymentLogic,
      )}
      {dlPair(
        "Volume Minimum Qu Payment Logic",
        rawCalculation.quarterlyVolumeMinimumPaymentLogic,
      )}
      {dlPair(
        "Non Volume Minimum Qu Payment Logic",
        rawCalculation.quarterlyNonVolumeMinimumPaymentLogic,
      )}
      {dlPair(
        "Settlement Quarterly Amount",
        rawCalculation.settlementQuarterlyAmount,
      )}
      {dlPair(
        "Settlement Quarterly Amount Logic",
        rawCalculation.settlementQuarterlyAmountLogic,
      )}
      {dlPair(
        "Termination Discount Logic",
        rawCalculation.terminationDiscountLogic,
      )}

      <h4>Capital Calc</h4>
      {dlPair("Supplier", capitalCalcCosts.dealer)}
      {dlPair("Rate Group", capitalCalcCosts.rateGroup)}
      {dlPair(
        "Volume Selection",
        <Format.Number
          value={capitalCalcCosts.volumeSelection}
          decimalPlaces={1}
          thousandSeparator
        />,
      )}
      {dlPair(
        "Total Minimum Quarterly Rental",
        currencyAmount(capitalCalcCosts.totalMinQuarterlyRental),
      )}
      {dlPair(
        "Schedule Settlement",
        currencyAmount(capitalCalcCosts.scheduleSettlementTotal),
      )}
      {dlPair(
        "Total Capital Costs",
        currencyAmount(capitalCalcCosts.totalCapitalCosts),
      )}
      {dlPair(
        "Quarterly Device Extra Cost",
        currencyAmount(capitalCalcCosts.quarterlyDeviceExtraCost),
      )}
      {dlPair(
        "Quarterly Device Extra Cost Logic",
        capitalCalcCosts.quarterlyDeviceExtraCostLogic,
      )}

      <h5>Items</h5>
      {capitalCalcCosts.itemCosts &&
        capitalCalcCosts.itemCosts.map((i, idx) => (
          <div className="capital-calc-item" key={idx}>
            {dlPair("Funder Type", FunderTypeDescription[i.funderType])}
            {isMssa &&
              dlPair(
                t("calcTableType"),
                calcTableTypeDescription[i.calcTableType],
              )}
            {dlPair("Finance Type", i.financeType)}
            {dlPair("Payment Method", capitalCalcCosts.paymentMethod)}
            {dlPair("Finance Selection", i.financeSelection)}
            {dlPair(
              "IP Syndicate Liquidity Charge",
              <Format.Number
                value={i.ipSyndicateLiquidityCharge}
                decimalPlaces={2}
                thousandSeparator
              />,
            )}
            {dlPair(
              "Yield Rate",
              <Format.Number
                value={i.yieldRate}
                decimalPlaces={{ min: 0, max: 3 }}
                thousandSeparator
              />,
            )}
            {dlPair(
              "Liquidity Charge Total %",
              <Format.Number
                value={i.liquidityChargeTotalPercentage}
                decimalPlaces={2}
                thousandSeparator
              />,
            )}
            {dlPair(
              "Rate Group RRP Percentage",
              <Format.Number
                value={i.rateGroupRrpPercentage}
                decimalPlaces={2}
                thousandSeparator
              />,
            )}
            {dlPair(
              "Max Product Capital Allowed",
              currencyAmount(i.maxProductCapitalAllowed),
            )}
            {dlPair("Gross Sales Rate", <Format.Number value={i.rate} />)}
            {dlPair("Yield Band", i.yieldBand)}
            {dlPair("Total Capital Costs", currencyAmount(i.totalCapitalCosts))}
            {dlPair(
              "Minimum Quarterly Rental",
              currencyAmount(i.minimumQuarterlyRental),
            )}
            {dlPair(
              "Min Quarterly Rental Logic",
              i.minimumQuarterlyRentalLogic,
            )}
            {dlPair(
              "Schedule Settlement",
              currencyAmount(i.scheduleSettlement),
            )}
            {dlPair("Schedule Settlement Logic", i.scheduleSettlementLogic)}
            {renderDealerCalc(i.dealerCalcCostResult)}
            {renderCofundedCalc(i.cofundedCalcCostResult)}
          </div>
        ))}

      <h4>Services</h4>
      {dlPair(
        "Sum of selected services",
        currencyAmount(servicesCost.servicesSum || 0),
      )}
      {dlPair(
        "Services total",
        currencyAmount(servicesCost.servicesTotal || 0),
      )}
      {dlPair("Services total logic", servicesCost.servicesTotalLogic)}
      {dlPair("Cost per click", unitAmount(servicesCost.costPerClick))}
      {dlPair("Cost Per Click Logic", servicesCost.costPerClickLogic)}
      {dlPair(
        "Min Quarterly Rental",
        currencyAmount(servicesCost.minQuarterlyRental || 0),
      )}
      {dlPair(
        "Min Quarterly Rental Logic",
        servicesCost.minQuarterlyRentalLogic,
      )}
      {dlPair(
        "Schedule Settlement",
        currencyAmount(servicesCost.scheduleSettlementTotal || 0),
      )}
      {dlPair(
        "Schedule Settlement Logic",
        servicesCost.scheduleSettlementTotalLogic,
      )}

      <h4>Schedule</h4>
      {dlPair(
        "Min Quarterly Payment",
        currencyAmount(schedule.minQuarterlyPayment),
      )}
      {dlPair(
        "Schedule Settlement",
        currencyAmount(schedule.scheduleSettlementTotal),
      )}
      <h5>Items</h5>
      {renderScheduleItems(schedule.itemCosts)}
      <h5>Large Format Items</h5>
      {renderScheduleItems(schedule.largeFormatItemCosts)}

      <h4>Paper Inclusive</h4>
      {dlPair(
        "TMVSA Amount / Min Qu Payment",
        currencyAmount(paperInclusiveCosts.minQuarterlyPayment),
      )}
      {dlPair(
        "Min Quarterly Payment Logic",
        paperInclusiveCosts.minQuarterlyPaymentLogic,
      )}
      {dlPair("A4 Paper Cost", currencyAmount(paperInclusiveCosts.a4PaperCost))}
      {dlPair("A3 Paper Cost", currencyAmount(paperInclusiveCosts.a3PaperCost))}
      {dlPair("Paper Price", currencyAmount(paperInclusiveCosts.paperPrice))}
      {dlPair("Paper Price Logic", paperInclusiveCosts.paperPriceLogic)}
      {dlPair(
        "A4 Paper Price",
        currencyAmount(paperInclusiveCosts.a4PaperPrice),
      )}
      {dlPair("A4 Paper Price Logic", paperInclusiveCosts.a4PaperPriceLogic)}
      {dlPair(
        "A3 Paper Price",
        currencyAmount(paperInclusiveCosts.a3PaperPrice),
      )}
      {dlPair("A3 Paper Price Logic", paperInclusiveCosts.a3PaperPriceLogic)}
      {dlPair("Units", paperInclusiveCosts.units)}
      {dlPair("Units Logic", paperInclusiveCosts.unitsLogic)}
      {dlPair("Cost per Unit", unitAmount(paperInclusiveCosts.costPerUnit))}
      {dlPair("Cost per Unit Logic", paperInclusiveCosts.costPerUnitLogic)}
      {dlPair(
        "Current Spend Total Amount",
        currencyAmount(paperInclusiveCosts.currentSpendTotalAmount),
      )}
      {dlPair(
        "Current Spend Total Amount Logic",
        paperInclusiveCosts.currentSpendTotalAmountLogic,
      )}
      {dlPair(
        "Total Included Min Del Charge",
        currencyAmount(paperInclusiveCosts.totalIncludedMinimumDeliveryCharge),
      )}
      {dlPair(
        "Total Included Min Del Charge Logic",
        paperInclusiveCosts.totalIncludedMinimumDeliveryChargeLogic,
      )}
      {dlPair(
        "Quarterly Contract Volume",
        <Format.Number
          value={paperInclusiveCosts.quarterlyContractVolume}
          decimalPlaces={0}
          thousandSeparator
        />,
      )}
      {dlPair(
        "Quarterly Contract Volume Logic",
        paperInclusiveCosts.quarterlyContractVolumeLogic,
      )}
      {dlPair(
        "Contract Volume In Boxed Reams",
        paperInclusiveCosts.contractVolumeInBoxedReams,
      )}
      {dlPair(
        "Contract Volume In Boxed Reams Logic",
        paperInclusiveCosts.contractVolumeInBoxedReamsLogic,
      )}
      {dlPair(
        "Min Diff To Create In TMVSA",
        currencyAmount(paperInclusiveCosts.minimumDifferenceToCreateInAmethyst),
      )}
      {dlPair(
        "Min Diff To Create In TMVSA Logic",
        paperInclusiveCosts.minimumDifferenceToCreateInAmethystLogic,
      )}
      {dlPair(
        "Likely Min Gross Margin",
        currencyAmount(paperInclusiveCosts.likelyMinimumGrossMargin),
      )}
      {dlPair(
        "Likely Min Gross Margin Logic",
        paperInclusiveCosts.likelyMinimumGrossMarginLogic,
      )}
      {dlPair("Full Boxes", paperInclusiveCosts.fullBoxes)}
      {dlPair("Full Boxes Logic", paperInclusiveCosts.fullBoxesLogic)}

      {renderFmvCalc(fmvs, calcTableTypeDescription)}
    </div>
  );
};

const renderFmvCalc = (fmvs, calcTableTypeDescription) =>
  fmvs == null ? null : (
    <React.Fragment>
      <h4>FMV</h4>
      {fmvs.map((fmv, idx) => (
        <div className="capital-calc-item" key={idx}>
          {dlPair("Funder Type", FunderTypeDescription[fmv.funderType])}
          {dlPair(
            "Calc Table Type",
            calcTableTypeDescription[fmv.calcTableType],
          )}
          {dlPair("Finance Type", fmv.financeType)}
          {dlPair("FMV Type", fmv.fmvType)}

          <h5>Adjusted Rates</h5>
          {renderFmvAdjusted("Overall", fmv.adjustedOverall)}
          {renderFmvAdjusted("IPS %", fmv.adjustedIpsPercentage)}
          {renderFmvAdjusted("IPS Protection", fmv.adjustedIpsProtection)}
          {renderFmvAdjusted("Supplier Net", fmv.adjustedDealerNet)}
          {dlPair(`Rate Used for GSR`, fmv.rateUsedForGSR)}
          {renderFmvAdjusted("GSR", fmv.adjustedGsr)}

          <h5>Future Values</h5>
          {renderFutureValue("Full FMV", fmv.fullFmvFutureValues)}
          {renderFutureValue("Of RRP", fmv.ofRrpFutureValues)}
          {renderFutureValue(
            "Of Total CAPITAL",
            fmv.ofTotalCapitalFutureValues,
          )}

          <h5>RRP Allowance</h5>
          {dlPair("RRP Allowance %", fmv.rrpAllowancePercentage)}
          {dlPair("of RRP %", fmv.ofRrpPercentage, fmv.ofRrpPercentageLogic)}
          {dlPair(
            "of Total Capital %",
            fmv.ofTotalCapitalPercentage,
            fmv.ofTotalCapitalPercentageLogic,
          )}

          <h5>Used figures</h5>
          {dlPair("Used %", fmv.fmvUsedPercentage, fmv.fmvUsedPercentageLogic)}
          {dlPair(
            "Used Amount",
            <Format.Number
              format={process.env.CURRENCY_CODE}
              value={fmv.fmvUsedAmount}
            />,
            fmv.fmvUsedAmountLogic,
          )}
        </div>
      ))}
    </React.Fragment>
  );

const renderFmvAdjusted = (name, adjusted) => (
  <React.Fragment>
    {dlPair(
      `${name} blended IP rate`,
      adjusted.blendedIpRate,
      adjusted.blendedIpRateLogic,
    )}
    {dlPair(
      `${name} nett CFA`,
      <Format.Number
        format={process.env.CURRENCY_CODE}
        value={adjusted.nettCfa}
      />,
      adjusted.nettCfaLogic,
    )}
  </React.Fragment>
);

const renderFutureValue = (name, fv) => (
  <React.Fragment>
    {dlPair(`${name} Percentage`, fv.percentage, fv.percentageLogic)}
    {dlPair(
      `${name} Amount`,
      <Format.Number format={process.env.CURRENCY_CODE} value={fv.amount} />,
      fv.amountLogic,
    )}
  </React.Fragment>
);

const renderDealerCalc = (r) =>
  r == null ? null : (
    <div className="funder-calc">
      <h4>Supplier Funded Calc</h4>

      {dlPair(
        "IPS % of Yield",
        <Format.Number
          value={r.ipSyndicatePercentageYield}
          decimalPlaces={{ min: 0, max: 3 }}
          suffix="%"
        />,
        r.ipSyndicatePercentageYieldLogic,
      )}
      {dlPair(
        "IPS %",
        <Format.Number
          value={r.ipSyndicatePercentage}
          decimalPlaces={2}
          suffix="%"
        />,
        r.ipSyndicatePercentageLogic,
      )}
      {dlPair(
        "Supplier %",
        <Format.Number
          value={r.dealerPercentage}
          decimalPlaces={2}
          suffix="%"
        />,
        r.dealerPercentageLogic,
      )}
      {dlPair(
        "IPS Liq Charge %",
        <Format.Number
          value={r.ipSyndicateLiquidityCharge}
          decimalPlaces={2}
          suffix="%"
        />,
        r.ipSyndicateLiquidityChargeLogic,
      )}
      {dlPair(
        "IPS Liq Charge amount",
        <Format.Number
          value={r.ipsLiquidityChargeAmount}
          format={process.env.CURRENCY_CODE}
        />,
        r.ipsLiquidityChargeAmountLogic,
      )}
      {dlPair(
        "IPS % of rental %",
        <Format.Number
          value={r.ipsPercentageOfRental}
          decimalPlaces={{ min: 0, max: 3 }}
          suffix="%"
        />,
        r.ipsPercentageOfRentalLogic,
      )}
      {dlPair(
        "Rental Charge",
        <Format.Number
          value={r.rentalCharge}
          format={process.env.CURRENCY_CODE}
        />,
      )}
      {dlPair(
        "Net Charge",
        <Format.Number
          value={r.netCharge}
          format={process.env.CURRENCY_CODE}
        />,
        r.netChargeLogic,
      )}
      {dlPair(
        "Supplier Net",
        <Format.Number
          value={r.dealerNet}
          format={process.env.CURRENCY_CODE}
        />,
        r.dealerNetLogic,
      )}
      {dlPair(
        "Additional charge",
        <Format.Number
          value={r.additionalCharge}
          format={process.env.CURRENCY_CODE}
        />,
        r.additionalChargeLogic,
      )}
      {dlPair(
        "IPS Share of Yield",
        <Format.Number
          value={r.ipSyndicateShareOfYield}
          format={process.env.CURRENCY_CODE}
        />,
        r.ipSyndicateShareOfYieldLogic,
      )}
    </div>
  );

const renderCofundedCalc = (r) =>
  r == null ? null : (
    <div className="funder-calc">
      <h4>Cofunded Calc</h4>

      {dlPair(
        "Supplier Co-fund %",
        <Format.Number
          value={r.dealerCofundPercentage}
          decimalPlaces={2}
          suffix="%"
        />,
      )}
      {dlPair(
        "Rental used",
        <Format.Number
          value={r.rentalUsed}
          format={process.env.CURRENCY_CODE}
        />,
      )}
      {dlPair("Number of payments", r.totalPayments)}
      {dlPair(
        "Total rentals",
        <Format.Number
          value={r.totalRentals}
          format={process.env.CURRENCY_CODE}
        />,
        r.totalRentalsLogic,
      )}
      {dlPair(
        "Gross Sales Rate",
        <Format.Number value={r.grossSalesRate} decimalPlaces={2} />,
      )}
      {dlPair(
        "Normal Supplier Net Rate",
        <Format.Number value={r.normalDealerNetRate} decimalPlaces={2} />,
      )}
      {dlPair(
        "IPS Net Rate",
        <Format.Number value={r.ipSyndicateNetRate} decimalPlaces={2} />,
      )}
      {dlPair(
        "IPS LC",
        <Format.Number
          value={r.ipSyndicateLiquidityCharge}
          decimalPlaces={2}
          suffix="%"
        />,
      )}
      {dlPair(
        "Supplier LC",
        <Format.Number
          value={r.dealerLiquidityCharge}
          decimalPlaces={2}
          suffix="%"
        />,
      )}
      {cofundedCapitalRow("IPS capital", r.ipsCapitalRow)}
      {cofundedCapitalRow(
        "Total supplier cap & liq payout",
        r.dealerCapitalRow,
      )}
      {cofundedCapitalRow("Total capital entered", r.totalCapitalRow)}
      {dlPair(
        "Total payout",
        <Format.Number
          value={r.totalPayout}
          format={process.env.CURRENCY_CODE}
        />,
        r.totalPayoutLogic,
      )}
      {dlPair(
        "IPS diff at all %",
        <Format.Number
          value={r.ipSyndicateDifferenceAtAllPercentage}
          format={process.env.CURRENCY_CODE}
        />,
        r.ipSyndicateDifferenceAtAllPercentageLogic,
      )}
      {dlPair(
        "IPS diff amount",
        <Format.Number
          value={r.ipSyndicateDifferenceAmount}
          format={process.env.CURRENCY_CODE}
        />,
        r.ipSyndicateDifferenceAmountLogic,
      )}
      {dlPair(
        "Capital % at supplier payout",
        <Format.Number
          value={r.capitalPercentageAtDealerPayout}
          decimalPlaces={2}
          suffix="%"
        />,
        r.capitalPercentageAtDealerPayoutLogic,
      )}
      {dlPair(
        "Capital % at IPS payout",
        <Format.Number
          value={r.capitalPercentageAtIpsPayout}
          decimalPlaces={2}
          suffix="%"
        />,
        r.capitalPercentageAtIpsPayoutLogic,
      )}
      {dlPair(
        "Supplier 1/4 rental balance",
        <Format.Number
          value={r.dealerQuarterlyRentalBalance}
          format={process.env.CURRENCY_CODE}
        />,
        r.dealerQuarterlyRentalBalanceLogic,
      )}
    </div>
  );

const dlPair = (name, value, logic) => (
  <React.Fragment>
    <DataList.Item title={name}>{value}</DataList.Item>
    {logic && <DataList.Item title={`${name} logic`}>{logic}</DataList.Item>}
  </React.Fragment>
);

const cofundedCapitalRow = (name, row) => (
  <div className="cofunded-capital-row">
    <h4>{name}</h4>
    {dlPair(
      "Total",
      <Format.Number value={row.total} format={process.env.CURRENCY_CODE} />,
      row.totalLogic,
    )}
    {dlPair(
      "Funder amount",
      <Format.Number
        value={row.funderAmount}
        format={process.env.CURRENCY_CODE}
      />,
      row.funderAmountLogic,
    )}
    {dlPair(
      "Balance",
      <Format.Number value={row.balance} format={process.env.CURRENCY_CODE} />,
      row.balanceLogic,
    )}
  </div>
);

const renderScheduleItems = (items) =>
  items &&
  items.map((i, idx) => (
    <div className="capital-calc-item" key={idx}>
      {dlPair("Unit", i.unit)}
      {dlPair("Fixed Code", i.displayCode || "-")}
      {dlPair(
        "Min Quarterly Volume",
        <Format.Number value={i.minQuarterlyVolume} />,
      )}
      {dlPair("Capital Cost Per Click", unitAmount(i.capitalCostPerClick))}
      {dlPair("Capital CPC Logic", i.capitalCostPerClickLogic)}
      {dlPair("Cost per Unit", unitAmount(i.costPerUnit))}
      {dlPair("Cost Per Unit Logic", i.costPerUnitLogic)}
      {dlPair("Finance Cost per Click", unitAmount(i.financeCostPerClick))}
      {dlPair("Finance Cost per Click Logic", i.financeCostPerClickLogic)}
      {dlPair("Min Quarterly Payment", currencyAmount(i.minQuarterlyPayment))}
      {dlPair("Min Quarterly Payment Logic", i.minQuarterlyPaymentLogic)}
      {dlPair("Schedule Settlement", currencyAmount(i.scheduleSettlement))}
      {dlPair("Schedule Settlement Logic", i.scheduleSettlementLogic)}
    </div>
  ));

const mapStateToProps = (state) => {
  const { rawCalculation } = getSheetState(state);
  return { rawCalculation };
};

export default connect(mapStateToProps, null)(CalculationBreakdown);
